import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import NotificationCenter from '../NotificationCenter';

export default function Nav() {
  const { user, logout } = useAuth();
  const { cartItem } = useCart();
  const navigate  = useNavigate();
  const location  = useLocation();
  const [scrolled,   setScrolled]   = useState(false);
  const [q,          setQ]          = useState('');
  const [menuOpen,   setMenuOpen]   = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', h);
    return () => window.removeEventListener('scroll', h);
  }, []);

  // Close mobile menu on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const handleSearch = (e) => {
    e?.preventDefault();
    if (q.trim()) { navigate(`/browse?q=${encodeURIComponent(q)}`); setMobileOpen(false); }
  };

  const isActive = (path) => location.pathname === path;

  const navLinks = [
    { label: 'Browse',         path: '/browse' },
    { label: 'Rentals',        path: '/browse?type=rent' },
    { label: 'Auctions',       path: '/browse?type=auction' },
    { label: 'For Sale',       path: '/browse?type=sale' },
    { label: '🔄 Barter',      path: '/barter' },
    { label: '🆓 Free',        path: '/free' },
    { label: '🔍 Looking For', path: '/looking-for' },
    { label: '🔴 Live',        path: '/live' },
    { label: 'How it Works',   path: '/how-it-works' },
  ];

  return (
    <>
      <nav className={`nav ${scrolled ? 'scrolled' : ''}`}>
        {/* Logo */}
        <div className="nav-logo" onClick={() => navigate('/')}>
          <span className="nav-logo-emoji">🍒</span>
          <span className="nav-logo-text">
            <span className="red">cherry</span>pkd
          </span>
        </div>

        {/* Search — desktop only */}
        <div className="nav-search hide-mobile" style={{ flex: 1, maxWidth: 380 }}>
          <span className="nav-search-icon">🔍</span>
          <input
            className="nav-search-input"
            placeholder="Search for anything near you..."
            value={q}
            onChange={e => setQ(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
          />
        </div>

        {/* Links — desktop only */}
        <div className="nav-links hide-mobile">
          {navLinks.map(l => (
            <button
              key={l.path}
              className={`nav-link ${isActive(l.path) ? 'active' : ''}`}
              onClick={() => navigate(l.path)}
              style={{ cursor: 'pointer', fontSize: 14, fontWeight: 500, color: 'var(--text-primary)', padding: '8px 12px', borderRadius: 6, transition: 'all .15s', background: 'transparent', border: 'none' }}
              onMouseEnter={e => { e.target.style.background = 'var(--cream)'; e.target.style.color = 'var(--cherry)'; }}
              onMouseLeave={e => { e.target.style.background = 'transparent'; e.target.style.color = 'var(--text-primary)'; }}
            >{l.label}</button>
          ))}
        </div>

        {/* Actions */}
        <div className="nav-actions">
          {/* Cart */}
          <button className="nav-icon-btn" onClick={() => navigate('/checkout')} title="Cart" style={{ position: 'relative' }}>
            🛒
            {cartItem && (
              <span style={{ position: 'absolute', top: -4, right: -4, width: 16, height: 16, borderRadius: '50%', background: 'var(--cherry)', color: 'white', fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>1</span>
            )}
          </button>

          {user ? (
            <>
              <NotificationCenter />

              <button className="nav-icon-btn hide-mobile" onClick={() => navigate('/messages')} title="Messages">💬</button>

              {/* Avatar / dropdown — desktop */}
              <div style={{ position: 'relative' }} className="hide-mobile">
                <button
                  onClick={() => setMenuOpen(o => !o)}
                  style={{ width: 36, height: 36, borderRadius: '50%', border: '2px solid var(--cherry)', background: user.avatarUrl ? 'transparent' : 'var(--cherry-light)', overflow: 'hidden', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, color: 'var(--cherry)' }}
                >
                  {user.avatarUrl
                    ? <img src={user.avatarUrl} alt={user.firstName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : user.firstName?.[0]?.toUpperCase()}
                </button>

                {menuOpen && (
                  <div
                    style={{ position: 'absolute', top: '100%', right: 0, marginTop: 8, background: 'white', border: '1px solid var(--border)', borderRadius: 12, padding: 8, minWidth: 200, boxShadow: 'var(--shadow-md)', zIndex: 200, animation: 'slideDown .2s ease' }}
                    onMouseLeave={() => setMenuOpen(false)}
                  >
                    <div style={{ padding: '8px 12px 10px', borderBottom: '1px solid var(--border)', marginBottom: 4 }}>
                      <div style={{ fontWeight: 700, fontSize: 14 }}>{user.firstName} {user.lastName}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-soft)' }}>{user.email}</div>
                    </div>
                    {[
                      { label: '📊 Dashboard',          path: '/dashboard' },
                      { label: '📋 My Listings',        path: '/dashboard/listings' },
                      { label: '📦 My Orders',          path: '/dashboard/orders' },
                      { label: '📅 My Bookings',        path: '/dashboard/bookings' },
                      { label: '❤️ Saved',              path: '/dashboard/saved' },
                      { label: '🔔 Watchlist & Alerts', path: '/dashboard/watchlist' },
                      { label: '🔍 Looking For Posts',  path: '/looking-for' },
                      { label: '💬 Messages',           path: '/messages' },
                      { label: '⭐ Subscription',       path: '/dashboard/subscription' },
                      { label: '⚙️ Account Settings',   path: '/settings' },
                    ].map(item => (
                      <button key={item.path} onClick={() => { navigate(item.path); setMenuOpen(false); }}
                        style={{ display: 'block', width: '100%', padding: '9px 12px', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--text-primary)', borderRadius: 8, transition: 'background .15s' }}
                        onMouseEnter={e => e.target.style.background = 'var(--cream)'}
                        onMouseLeave={e => e.target.style.background = 'none'}
                      >{item.label}</button>
                    ))}
                    {user.role === 'admin' && (
                      <button onClick={() => { navigate('/admin'); setMenuOpen(false); }}
                        style={{ display: 'block', width: '100%', padding: '9px 12px', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--cherry)', fontWeight: 700, borderRadius: 8 }}>
                        🛡️ Admin Panel
                      </button>
                    )}
                    <div style={{ borderTop: '1px solid var(--border)', marginTop: 4, paddingTop: 4 }}>
                      <button onClick={() => { logout(); setMenuOpen(false); navigate('/'); }}
                        style={{ display: 'block', width: '100%', padding: '9px 12px', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--text-soft)', borderRadius: 8 }}>
                        🚪 Log out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <button className="btn btn-ghost btn-sm hide-mobile" onClick={() => navigate('/login')}>Sign in</button>
              <button className="btn btn-cherry btn-sm hide-mobile" onClick={() => navigate('/signup')}>Get started</button>
            </>
          )}

          <button className="btn btn-cherry btn-sm hide-mobile" style={{ marginLeft: 4 }} onClick={() => navigate('/list')}>
            + List item
          </button>

          {/* Hamburger — mobile only */}
          <button
            className="show-mobile"
            onClick={() => setMobileOpen(o => !o)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '6px 4px', display: 'flex', flexDirection: 'column', gap: 5, alignItems: 'center', justifyContent: 'center' }}
            aria-label="Menu"
          >
            <span style={{ display: 'block', width: 22, height: 2, background: mobileOpen ? 'transparent' : 'var(--charcoal)', borderRadius: 2, transition: 'all .2s' }} />
            <span style={{ display: 'block', width: 22, height: 2, background: 'var(--charcoal)', borderRadius: 2, transition: 'all .2s', transform: mobileOpen ? 'rotate(45deg) translate(5px, -5px)' : 'none' }} />
            <span style={{ display: 'block', width: 22, height: 2, background: 'var(--charcoal)', borderRadius: 2, transition: 'all .2s', transform: mobileOpen ? 'rotate(-45deg) translate(5px, 5px)' : 'none' }} />
          </button>
        </div>
      </nav>

      {/* ── Mobile Menu Overlay ────────────────────────────── */}
      {mobileOpen && (
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 999, display: 'flex', flexDirection: 'column' }}
          onClick={e => { if (e.target === e.currentTarget) setMobileOpen(false); }}
        >
          {/* Backdrop */}
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,.4)' }} onClick={() => setMobileOpen(false)} />

          {/* Drawer */}
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, background: 'white', maxHeight: '92vh', overflowY: 'auto', borderBottomLeftRadius: 20, borderBottomRightRadius: 20, boxShadow: '0 8px 40px rgba(0,0,0,.2)', paddingBottom: 24 }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
              <div style={{ fontFamily: "'Playfair Display',serif", fontWeight: 700, fontSize: 20 }}>
                <span style={{ color: 'var(--cherry)' }}>cherry</span>pkd
              </div>
              <button onClick={() => setMobileOpen(false)} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: 'var(--text-soft)', padding: 4 }}>✕</button>
            </div>

            {/* Mobile Search */}
            <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)' }}>
              <form onSubmit={handleSearch} style={{ display: 'flex', gap: 8 }}>
                <input
                  style={{ flex: 1, padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 14, fontFamily: "'DM Sans',sans-serif", outline: 'none' }}
                  placeholder="Search..."
                  value={q}
                  onChange={e => setQ(e.target.value)}
                />
                <button type="submit" className="btn btn-cherry btn-sm">Go</button>
              </form>
            </div>

            {/* User info (if logged in) */}
            {user && (
              <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 40, height: 40, borderRadius: '50%', border: '2px solid var(--cherry)', background: 'var(--cherry-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 700, color: 'var(--cherry)', overflow: 'hidden', flexShrink: 0 }}>
                  {user.avatarUrl ? <img src={user.avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : user.firstName?.[0]?.toUpperCase()}
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>{user.firstName} {user.lastName}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-soft)' }}>{user.email}</div>
                </div>
              </div>
            )}

            {/* Nav links */}
            <div style={{ padding: '8px 12px' }}>
              {navLinks.map(l => (
                <button key={l.path} onClick={() => navigate(l.path)}
                  style={{ display: 'block', width: '100%', padding: '12px 12px', textAlign: 'left', background: isActive(l.path) ? 'var(--cherry-light)' : 'none', border: 'none', cursor: 'pointer', fontSize: 15, color: isActive(l.path) ? 'var(--cherry)' : 'var(--text-primary)', borderRadius: 10, fontFamily: "'DM Sans',sans-serif", fontWeight: isActive(l.path) ? 700 : 500, marginBottom: 2 }}>
                  {l.label}
                </button>
              ))}
            </div>

            {/* Account links (logged in) */}
            {user && (
              <div style={{ borderTop: '1px solid var(--border)', padding: '8px 12px', marginTop: 4 }}>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--text-soft)', padding: '6px 12px 10px' }}>Account</div>
                {[
                  { label: '📊 Dashboard',          path: '/dashboard' },
                  { label: '📋 My Listings',        path: '/dashboard/listings' },
                  { label: '📦 My Orders',          path: '/dashboard/orders' },
                  { label: '💬 Messages',           path: '/messages' },
                  { label: '🔔 Watchlist & Alerts', path: '/dashboard/watchlist' },
                  { label: '⚙️ Settings',           path: '/settings' },
                ].map(item => (
                  <button key={item.path} onClick={() => navigate(item.path)}
                    style={{ display: 'block', width: '100%', padding: '11px 12px', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: 'var(--text-primary)', borderRadius: 10, marginBottom: 2 }}>
                    {item.label}
                  </button>
                ))}
                {user.role === 'admin' && (
                  <button onClick={() => navigate('/admin')}
                    style={{ display: 'block', width: '100%', padding: '11px 12px', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: 'var(--cherry)', fontWeight: 700, borderRadius: 10 }}>
                    🛡️ Admin Panel
                  </button>
                )}
                <button onClick={() => { logout(); setMobileOpen(false); navigate('/'); }}
                  style={{ display: 'block', width: '100%', padding: '11px 12px', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: 'var(--text-soft)', borderRadius: 10 }}>
                  🚪 Log out
                </button>
              </div>
            )}

            {/* CTA buttons */}
            <div style={{ padding: '16px 20px', borderTop: '1px solid var(--border)', display: 'flex', gap: 10 }}>
              <button className="btn btn-cherry btn-full" onClick={() => navigate('/list')} style={{ flex: 1 }}>+ List item</button>
              {!user && <button className="btn btn-ghost btn-full" onClick={() => navigate('/login')} style={{ flex: 1 }}>Sign in</button>}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
